import { useEffect, useState } from "react";
import { SwiperSlide } from "swiper/react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Container,
  Grid,
  LinearProgress,
  Stack,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AddIcon from "@mui/icons-material/Add";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { Download } from "@mui/icons-material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArticleReader from "./ArticleReader";
import AudioPlayer from "./AudioPlayer";
import PresentationViewer from "./PresentationViewer";
import LearningVideoPlayer from "./LearningVideoPlayer";
import Infographics from "./Infographics";
import LearningCourseDetails from "./LearningCourseDetails";
import LearningContentContainer from "../../container/learningContent.container";
import VSNButton from "../../shared/VSNButton";
import VSNLoader from "../../shared/VSNLoader";
import VSNImage from "../../shared/VSNImage";
import VSNTypography from "../../shared/VSNTypography";
import vasana from "../../assets/landing/svg/vasana-white.svg";
import { LearningStyle } from "./LearningStyle";
import { VSNSwiper } from "../../shared/VSNSwiper";
import {
  commingUpNextData,
  downloadData,
  practicesData,
} from "./LearningConstant";
import VSNPaper from "../../shared/VSNPaper";
import { Cap, LearningVideoIcon } from "../../shared/icon";
import VSNBreadcrumbs from "../../shared/VSNBreadcrumbs";
import Learning from "./Learning";

export default function LearningContentType() {
  const { type, course_id, lesson_id } = useParams();

  const classes = LearningStyle();
  const navigate = useNavigate();

  const {
    learningContentData,
    LearningContentLoader,
    handleContentDownload,
    downloadingIndex,
    from,
    name,
  } = LearningContentContainer({ type, course_id, lesson_id });

  useEffect(() => {
    if (
      lesson_id &&
      type !== "course" &&
      !LearningContentLoader &&
      learningContentData?.content_type
    ) {
      const actualType = learningContentData?.content_type?.toLowerCase();
      if (actualType !== type.toLowerCase()) {
        navigate(`/learning/content/${actualType}/${course_id}/${lesson_id}`, {
          replace: true,
        });
      }
    }
  }, [
    LearningContentLoader,
    learningContentData,
    type,
    course_id,
    lesson_id,
    navigate,
  ]);

  const prevCourseId = learningContentData?.previous_lesson_id;
  const nextCourseId = learningContentData?.next_lesson_id;

  const goToLesson = (newLessonId) => {
    navigate(`/learning/content/${type}/${course_id}/${newLessonId}`);
  };

  const renderComponent = () => {
    switch (type.toLowerCase()) {
      case "video":
        return (
          <LearningVideoPlayer
            data={learningContentData}
            loader={LearningContentLoader}
          />
        );
      case "article":
        return (
          <ArticleReader
            data={learningContentData}
            loader={LearningContentLoader}
          />
        );
      case "audio":
        return (
          <AudioPlayer
            data={learningContentData}
            loader={LearningContentLoader}
          />
        );
      case "presentation":
        return (
          <PresentationViewer
            data={learningContentData}
            loader={LearningContentLoader}
          />
        );
      case "course":
        return (
          <LearningCourseDetails
            data={learningContentData}
            course_id={course_id}
            loader={LearningContentLoader}
          />
        );
      case "infographic":
        return (
          <Infographics
            data={learningContentData}
            loader={LearningContentLoader}
          />
        );
      default:
        return <Learning />;
    }
  };

  let breadcrumbsRoutes = [];

  if (from === "categorys" || from === "learning") {
    breadcrumbsRoutes = [
      {
        path: "/learning",
        label: "Learning",
      },
      {
        path: `/learning/explore-all?from=${from}&id=2&name=${name}`,
        label: name,
      },
      {
        path: "/learning",
        label: learningContentData?.title || "Loading...",
      },
    ];
  } else {
    breadcrumbsRoutes = [
      {
        path: "/learning",
        label: "Learning",
      },
    ];

    if (learningContentData?.is_course) {
      if (
        ["video", "article", "audio", "infographic", "presentation"].includes(
          type
        ) &&
        lesson_id
      ) {
        breadcrumbsRoutes.push({
          path: `/learning/content/course/${course_id}`,
          label: learningContentData?.course_title || "Course",
        });
      }
    }

    breadcrumbsRoutes.push({
      label: learningContentData?.title || "Loading...",
    });
  }

  if (LearningContentLoader) {
    return <VSNLoader variant="transParent" />;
  }

  return (
    <Box p={{ sm: 4, xs: 2 }} key={type}>
      <Container maxWidth="xl" px={{ sm: 4, xs: 2 }}>
        <Stack
          direction="row"
          flexWrap="wrap"
          justifyContent="space-between"
          alignItems="center"
        >
          <VSNBreadcrumbs {...{ breadcrumbsRoutes }} />

          {/* <Stack
          direction="row"
          alignItems="center"
          className={classes.developmentProgress}
        >
          <Stack direction="row" alignItems="center">
            <Cap />
            <VSNTypography ml={1}>Personal Development for a Sa</VSNTypography>
          </Stack>
          <Stack flex={1} direction="row" alignItems="center">
            <LinearProgress variant="determinate" value={20} />
            <VSNTypography>20%</VSNTypography>
          </Stack>
        </Stack> */}
        </Stack>
      </Container>

      {(prevCourseId || nextCourseId) && (
        <Container maxWidth="xl" p={{ sm: 4, xs: 2 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems="center"
            gap={2}
            mt={2}
          >
            {prevCourseId && (
              <VSNButton
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                color="secondary"
                sx={{ px: 3, width: { xs: "100%", sm: "auto" } }}
                disabled={!prevCourseId}
                handleClick={() => goToLesson(prevCourseId)}
              >
                Previous Lesson
              </VSNButton>
            )}

            {nextCourseId && (
              <VSNButton
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  px: 3,
                  ml: "auto",
                  width: {
                    xs: "100%",
                    sm: "auto",
                  },
                }}
                disabled={!nextCourseId}
                handleClick={() => goToLesson(nextCourseId)}
              >
                Next Lesson
              </VSNButton>
            )}
          </Stack>
        </Container>
      )}
      {renderComponent()}

      {learningContentData?.downloadables?.length > 0 && (
        <Box mb={4} mt={6}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            className={classes.sliderHeader}
          >
            <VSNTypography variant="h2">Downloadables </VSNTypography>
            {/* <Link to="/">
              Download All <Download />
            </Link> */}
          </Stack>
          <Box className={classes.practiceSlider}>
            {/* <Box className="button-prev">
              <KeyboardArrowLeftIcon />
            </Box> */}
            <VSNSwiper
              pagination
              navigation={{
                nextEl: ".button-next",
                prevEl: ".button-prev",
              }}
              spaceBetween={5}
              breakpoints={{
                0: {
                  slidesPerView: 1,
                },
                640: {
                  slidesPerView: 2,
                },
                992: {
                  slidesPerView: 3,
                },
                1500: {
                  slidesPerView: 4,
                },
              }}
            >
              {learningContentData?.downloadables?.map((el, index) => (
                <SwiperSlide key={index}>
                  <Box className={classes.downloadBox} minHeight="230px">
                    <VSNImage src={el?.thumbnail} />

                    <Box
                      className={classes.logoIcon}
                      onClick={() => handleContentDownload(el, index)}
                      style={{ cursor: "pointer" }}
                    >
                      {downloadingIndex === index ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        <Download />
                      )}
                    </Box>

                    <Box className={classes.contentTitle}>
                      <VSNTypography variant="h5">{el?.title}</VSNTypography>
                    </Box>
                  </Box>
                </SwiperSlide>
              ))}
            </VSNSwiper>
            {/* <Box className="button-next">
              <ChevronRightIcon />
            </Box> */}
          </Box>
        </Box>
      )}

      {/* <Box mb={4}>
        <Stack className={classes.sliderHeader}>
          <VSNTypography variant="h2">Practices </VSNTypography>
        </Stack>
        <Box className={classes.practiceSlider}>
          <Box className="button-prev practices-prev">
            <KeyboardArrowLeftIcon />
          </Box>
          <VSNSwiper
            pagination
            navigation={{
              nextEl: ".practices-next",
              prevEl: ".practices-prev",
            }}
            breakpoints={{
              0: {
                slidesPerView: 1,
              },
              640: {
                slidesPerView: 2,
              },
              992: {
                slidesPerView: 3,
              },
              1200: {
                slidesPerView: 4,
              },
              1500: {
                slidesPerView: 5,
              },
            }}
          >
            {practicesData?.map((el, index) => (
              <SwiperSlide key={index}>
                <Box
                  className={classes.practiceBox}
                  style={{
                    backgroundColor: el.bgColor,
                    borderColor: el.borderColor,
                  }}
                >
                  {el?.icon}
                  <VSNTypography variant="h4">{el?.title}</VSNTypography>
                </Box>
              </SwiperSlide>
            ))}
          </VSNSwiper>
          <Box className="button-next practices-next">
            <ChevronRightIcon />
          </Box>
        </Box>
      </Box>

      <Stack className={classes.sliderHeader}>
        <VSNTypography variant="h2">Coming Up Next</VSNTypography>
        <Stack direction="row" justifyContent="space-between">
          <VSNTypography variant="h5">
            Discover your Recommended Content, a personalized mix of videos,
            articles, and podcasts curated just for you!
          </VSNTypography>
        </Stack>
      </Stack>

      <Grid container spacing={3}>
        {commingUpNextData?.map((el, index) => (
          <Grid item xs={12} sm={6} lg={3} key={el}>
            <Box sx={{ cursor: "pointer" }}>
              <VSNPaper className={classes.videosBox}>
                <VSNImage src={demoImage} />
                <Box className={classes.typeIcon}>
                  <LearningVideoIcon />
                </Box>

                <Box className={classes.logoIcon}>
                  <VSNImage src={vasana} />
                </Box>

                <Box className={classes.contentTitle}>
                  <VSNTypography variant="h5">{el?.title}</VSNTypography>
                </Box>

                <Box className={classes.addPlusIcon}>
                  <AddCircleIcon />
                </Box>
              </VSNPaper>
            </Box>
          </Grid>
        ))}
      </Grid> */}
    </Box>
  );
}
